import { useEffect, useReducer } from 'react';
import axiosClient from 'api/axiosClient';

/**
 * IA-1077: fetch the current admin's partner logo from `/admin/profileDetails` so the
 * top-left SideMenu logo always renders a *freshly* signed CloudFront URL.
 *
 * The logo stored in the `user` cookie at login is signed with a short (1h) TTL but the
 * cookie lives 8h, so after the first hour that cached signature expires and CloudFront
 * returns AccessDenied (broken logo). This endpoint is signed by the global signMediaUrls
 * middleware on every request, so the URL it returns is always valid. Fails silently — a
 * missing logo just falls back to the bundled default, it should never surface a toast.
 */
interface UseCurrentUserLogoState {
  logo: string | null;
  isLoading: boolean;
}

const initialState: UseCurrentUserLogoState = {
  logo: null,
  isLoading: false,
};

const reducer = (state: UseCurrentUserLogoState, payload: Partial<UseCurrentUserLogoState>) => ({
  ...state,
  ...payload,
});

export default function useCurrentUserLogo() {
  const [state, setState] = useReducer(reducer, initialState);

  useEffect(() => {
    let active = true;

    const fetchLogo = async () => {
      setState({ isLoading: true });
      try {
        const body = await axiosClient.get('/admin/profileDetails').then(res => res.data);
        if (active && !body?.error) {
          setState({ logo: body?.data?.logo ?? null });
        }
      } catch {
        // Silent: keep the current fallback logo rather than interrupt the page.
      } finally {
        if (active) {
          setState({ isLoading: false });
        }
      }
    };

    fetchLogo();

    return () => {
      active = false;
    };
  }, []);

  return state;
}
